/**
 * MUI Page Component
 * 
 * This component is the Material UI equivalent of the original Page component.
 * It renders the main page layout for the Room Designer application.
 */

import React, { forwardRef, Fragment, Suspense, useState } from 'react';
import { Box, Container } from '@mui/material';
import { useSelector } from 'react-redux';
import type { RootState } from 'types/redux/state';

import DesignerHeader from '../organisms/DesignerHeader';
import MuiDesignContainer from '../organisms/DesignContainer';
import MuiNavContainer from '../organisms/NavContainer';
import Spotlight from '../services/Spotlight';

interface PageProps {
  showChild?: boolean;
  onOpenDesignNameDialog?: () => void;
}

/**
 * MUI Page component
 * 
 * @param props - Component props
 * @param ref - Forwarded ref
 * @returns The MUI Page component
 */
const MuiPage = forwardRef<HTMLDivElement, PageProps>((props, ref) => {
  const projectName = useSelector((state: RootState) => state.container.project.name);
  const guideActiveSection = useSelector((state: RootState) => state.ui.spotlightTarget);
  const [silentSave, setSilentSave] = useState<boolean>(false);
  
  return (
    <Fragment>
      <Box 
        sx={{ 
          height: '100vh', 
          width: '100vw',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: 0,
          padding: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: '#F7F8FA',
        }}
      >
        <Suspense>
          {/* Full-width unified header at the top */}
          <DesignerHeader 
            showMyDesigns={true}
            projectName={projectName}
          />
          
          {/* Content area below header with NavContainer on left and DesignContainer on right */}
          <Box 
            sx={{ 
              display: 'flex', 
              height: 'calc(100vh - 56px)', // Subtract header height
              overflow: 'hidden',
              position: 'relative',
              zIndex: 'auto'
            }}
          >
            {/* NavContainer on the left */}
            <MuiNavContainer onOpenDesignNameDialog={props.onOpenDesignNameDialog} />
            
            {/* DesignContainer fills remaining space on the right */}
            {props.showChild && (
              <Box className="flex-grow-1" sx={{ overflow: 'hidden' }}>
                <MuiDesignContainer>{null}</MuiDesignContainer>
              </Box>
            )}
          </Box>
        </Suspense>
      </Box>
      <Spotlight />
    </Fragment>
  );
});

// Display name for debugging
MuiPage.displayName = 'MuiPage';

export default MuiPage;